import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { 
  Loader2, 
  Shield, 
  ArrowLeft, 
  User, 
  Key, 
  Bell, 
  AlertTriangle, 
  CheckCircle2, 
  Info, 
  Clock,
  Laptop,
  Globe,
  Eye,
  EyeOff,
  Lock,
  Edit,
  Mail,
  Calendar,
  Save,
  X,
  LogIn,
  UserCircle as UserIcon,
  BarChart3
} from "lucide-react";
import { MFASetup } from "@/components/mfa/mfa-setup";
import { MFAManagement } from "@/components/mfa/mfa-management";
import DynamicAccountActivity from "@/components/DynamicAccountActivity";
import { Redirect, useLocation, Link } from "wouter";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast, useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatPhoneNumber, stripPhoneNumber, validatePhoneNumber } from "@/lib/utils";
import type { GetUserDto } from "@server/routes/users/dto";
import { useMutation, useQuery } from "@tanstack/react-query";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { logoutAllDevices, logoutDevice } from "@/data-access/auth";
import { fetchUserSession, fetchUserUsageStats } from "@/data-access/users";

// List of countries for the dropdown
const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Australia", "Germany", 
  "France", "Japan", "China", "India", "Brazil", "Mexico", "South Africa",
  "Nigeria", "Egypt", "Russia", "Italy", "Spain", "Netherlands", "Sweden",
  "South Korea", "Singapore", "New Zealand", "Other"
];

// Password Form Component
function PasswordForm() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  // Get the current user for verification
  const { user } = useAuth();
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Calculate password strength if the new password field is being updated
    if (name === "newPassword") {
      calculatePasswordStrength(value);
    }
  };
  
  // Calculate password strength
  const calculatePasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    
    // Length check
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) strength += 1; // Has uppercase
    if (/[a-z]/.test(password)) strength += 1; // Has lowercase
    if (/[0-9]/.test(password)) strength += 1; // Has number
    if (/[^A-Za-z0-9]/.test(password)) strength += 1; // Has special char
    
    setPasswordStrength(Math.min(strength, 5)); // Cap at 5 for 5 levels
  };
  
  // Get password strength color
  const getStrengthColor = () => {
    if (passwordStrength <= 1) return "bg-red-500";
    if (passwordStrength <= 2) return "bg-orange-500";
    if (passwordStrength <= 3) return "bg-yellow-500";
    if (passwordStrength <= 4) return "bg-green-400";
    return "bg-green-600";
  };
  
  // Get password strength label
  const getStrengthLabel = () => {
    if (passwordStrength <= 1) return "Very Weak";
    if (passwordStrength <= 2) return "Weak";
    if (passwordStrength <= 3) return "Medium";
    if (passwordStrength <= 4) return "Strong";
    return "Very Strong";
  };
  
  // Form validation
  const isValid = () => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?].*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/;
    return (
      formData.currentPassword.length > 0 &&
      formData.newPassword.length >= 12 &&
      passwordRegex.test(formData.newPassword) &&
      formData.newPassword === formData.confirmPassword
    );
  };
  
  // Simplified form submission with direct password update approach
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid()) {
      toast({
        title: "Validation Error",
        description: 
          formData.newPassword.length < 12 
            ? "New password must be at least 12 characters long" 
            : formData.newPassword !== formData.confirmPassword 
              ? "New password and confirmation do not match" 
              : "Password must contain at least 1 uppercase letter, 1 lowercase letter, 2 numbers, and 2 special characters",
        variant: "destructive",
      });
      return;
    }
    
    // Check if we have the user data to send with the request
    if (!user || !user.username) {
      toast({
        title: "Error",
        description: "Could not verify your account. Please try logging in again.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Add username to the request for validation on the server side
      const requestData = {
        ...formData,
        username: user.username
      };
      
      // Now send the password update request with the username included
      const response = await fetch("/api/user/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData)
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        toast({
          title: "Update failed",
          description: responseData.message || "Failed to update password",
          variant: "destructive",
        });
        return;
      }
      
      // Success case
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated.",
        variant: "default",
      });
      
      // Clear the form
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };
  
  return (
    <form className="space-y-8" onSubmit={handleSubmit}>
      {/* Password Form Header */}
      <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
          <Lock className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Change Password
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Update your password to keep your account secure
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
            Current Password
          </label>
          <div className="relative">
            <Input 
              className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] pr-10"
              type={showPassword ? "text" : "password"} 
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder="Enter your current password" 
            />
            <Button 
              type="button"
              variant="ghost" 
              size="sm"
              className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        
        <div>
          <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
            New Password
          </label>
          <div className="relative">
            <Input 
              className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] pr-10"
              type={showNewPassword ? "text" : "password"} 
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter your new password" 
            />
            <Button 
              type="button"
              variant="ghost" 
              size="sm"
              className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Password strength indicator */}
          {formData.newPassword && (
            <div className="mt-2">
              <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getStrengthColor()} transition-all duration-300`} 
                  style={{ width: `${passwordStrength * 20}%` }}
                ></div>
              </div>
              <p className="text-xs mt-1 text-gray-500 dark:text-gray-400 flex justify-between">
                <span>Password strength: {getStrengthLabel()}</span>
                <span>Min 12 characters</span>
              </p>
            </div>
          )}
        </div>
        
        <div>
          <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
            Confirm New Password
          </label>
          <div className="relative">
            <Input 
              className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] pr-10"
              type={showConfirmPassword ? "text" : "password"} 
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your new password" 
            />
            <Button 
              type="button"
              variant="ghost" 
              size="sm"
              className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Password match indicator */}
          {formData.confirmPassword && (
            <p className={`text-xs mt-1 ${formData.newPassword === formData.confirmPassword ? "text-green-500" : "text-red-500"}`}>
              {formData.newPassword === formData.confirmPassword 
                ? "Passwords match" 
                : "Passwords do not match"}
            </p>
          )}
        </div>
      </div>
      
      <div>
        <Button 
          type="submit"
          size="sm"
          disabled={!isValid()}
        >
          <Lock className="h-4 w-4" />
          Update Password
        </Button>
      </div>
      
      <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-4 border border-blue-100 dark:border-blue-800">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-400 dark:text-blue-300 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">Password Tips</h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
              <ul className="list-disc pl-5 space-y-1">
                <li>Use at least 12 characters</li>
                <li>Include uppercase and lowercase letters</li>
                <li>At least 2 numbers</li>
                <li>At least 2 special characters</li>
                <li>Avoid using easily guessable information</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

// Security Tab Content Component
function SecurityTabContent({ 
  user, 
  showMFASetup, 
  setShowMFASetup 
}: { 
  user: GetUserDto; 
  showMFASetup: boolean; 
  setShowMFASetup: (show: boolean) => void; 
}) {
  const { logoutMutation } = useAuth();
  const { data: userSession, isLoading: isUserSessionLoading } = useQuery({
    queryKey: ['userSession'],
    queryFn: fetchUserSession,
  });

  const handleLogoutAllDevices = async () => {
    await logoutAllDevices();
    await logoutMutation.mutateAsync();
    // show toast notification
    toast({
      title: "Logged out from all devices",
      description: "You have been logged out from all devices",
      variant: "default",
    });
  };

  const handleLogoutDevice = async (sessionId: string) => {
    await logoutDevice(sessionId);
    // invalidate the user session query
    queryClient.invalidateQueries({ queryKey: ['userSession'] });
    // show toast notification
    toast({
      title: "Logged out from device",
      description: "You have been logged out from this device",
      variant: "default",
    });
  };

  if (isUserSessionLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-6">
      {showMFASetup ? (
        <MFASetup onCancel={() => setShowMFASetup(false)} />
      ) : (
        <div className="space-y-8">
          {/* Security Form Header */}
          <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div className="h-12 w-12 bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Security
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Strengthen your security settings to keep your account protected
              </p>
            </div>
          </div>
          <div>
            <MFAManagement 
              onSetupMFA={() => setShowMFASetup(true)} 
            />
          </div>
          
          <div>
            <h4 className="text-base font-semibold mb-4 text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <Laptop className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              Logged In Devices
            </h4>
            
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 space-y-4">
              {userSession?.loggedInDevices.map((device, index) => (
                <div key={device.userId} className={`flex justify-between items-center ${index < userSession.loggedInDevices.length - 1 ? "border-b border-gray-200 dark:border-gray-700 pb-3" : ""}`}>
                  <p>{index + 1}. {device.browser} on {device.operatingSystem} {device.deviceType} {device.current ? <Badge variant="secondary">Current Device</Badge> : ""}</p>
                  <Button size="sm" onClick={() => handleLogoutDevice(device.sessionId)}>
                    Log Out
                  </Button>
                </div>
              ))}
              
            </div>
          </div>
          
          <div>
            <h4 className="text-base font-semibold mb-4 text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              Recent Account Activity
            </h4>
            
            {/* Dynamic account activity display with real-time data */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 space-y-4">
              <DynamicAccountActivity userId={user.id} />
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
}

// Notifications Tab Content Component
function NotificationsTabContent() {
  const [settings, setSettings] = useState({
    accountAlerts: true,
    blockchainNews: true,
    marketingEmails: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({
    accountAlerts: false,
    blockchainNews: false,
    marketingEmails: false,
  });

  // Load notification settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch('/api/user/notification-settings', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setSettings({
            accountAlerts: data.accountAlerts,
            blockchainNews: data.blockchainNews,
            marketingEmails: data.marketingEmails,
          });
        }
      } catch (error) {
        console.error('Failed to load notification settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  const updateSetting = async (key: keyof typeof settings, value: boolean) => {
    // Update local state immediately for responsive UI
    setSettings(prev => ({ ...prev, [key]: value }));
    
    // Set saving state for this specific setting
    setSavingStates(prev => ({ ...prev, [key]: true }));
    
    try {
      // Create the updated settings object
      const updatedSettings = { ...settings, [key]: value };
      
      const response = await fetch('/api/user/notification-settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updatedSettings),
      });

      if (!response.ok) {
        // Revert the change if the API call failed
        setSettings(prev => ({ ...prev, [key]: !value }));
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      // Could add toast notification here for error feedback
    } finally {
      // Clear saving state for this specific setting
      setSavingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Notifications Form Header */}
        <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-gray-700">
          <div className="h-12 w-12 bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 rounded-full flex items-center justify-center">
            <Bell className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Notifications
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Customize your notifications to stay updated on important activity
            </p>
          </div>
        </div>
        
        <div>
          <h4 className="text-base font-semibold mb-4 text-gray-800 dark:text-gray-200">
            Email Notifications
          </h4>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Account alerts
                  {savingStates.accountAlerts && (
                    <span className="ml-2 text-xs text-blue-500">Saving...</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Receive notifications about security events
                </div>
              </div>
              <Switch 
                checked={settings.accountAlerts}
                disabled={savingStates.accountAlerts}
                onCheckedChange={(checked) => updateSetting('accountAlerts', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Blockchain news
                  {savingStates.blockchainNews && (
                    <span className="ml-2 text-xs text-blue-500">Saving...</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Get the latest news and updates
                </div>
              </div>
              <Switch 
                checked={settings.blockchainNews}
                disabled={savingStates.blockchainNews}
                onCheckedChange={(checked) => updateSetting('blockchainNews', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Marketing emails
                  {savingStates.marketingEmails && (
                    <span className="ml-2 text-xs text-blue-500">Saving...</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Receive offers and promotions
                </div>
              </div>
              <Switch 
                checked={settings.marketingEmails}
                disabled={savingStates.marketingEmails}
                onCheckedChange={(checked) => updateSetting('marketingEmails', checked)}
              />
            </div>
          </div>
        </div>
        
        {/* <div>
          <h4 className="text-base font-semibold mb-4 text-gray-800 dark:text-gray-200">
            Wallet Alerts
          </h4>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Email notifications
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Send wallet alerts to your email
                </div>
              </div>
              <Switch defaultChecked />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  High-value transactions
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Only alert on transactions above 0.5 BTC
                </div>
              </div>
              <Switch />
            </div>
          </div>
        </div> */}
        

      </div>
    </div>
  );
}

// Usage Tab Content Component
function UsageTabContent() {
  const { data: usageStats, isLoading, error } = useQuery({
    queryKey: ['userUsage'],
    queryFn: fetchUserUsageStats,
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !usageStats) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Unable to load usage statistics</p>
          <p className="text-sm">Please try again later</p>
        </div>
      </div>
    );
  }

  const formatResetDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getProgressColor = (current: number, limit: number) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-orange-500";
    return "bg-blue-500";
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Usage Form Header */}
        <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-gray-700">
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
            <BarChart3 className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Usage Statistics
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {usageStats.isAdmin 
                ? "As an admin, you have unlimited usage for all features"
                : "Track your monthly usage limits and remaining allowances"
              }
            </p>
          </div>
        </div>
        
        {/* Usage Stats Grid */}
        <div className="grid gap-6">
          {/* Searches */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                  Searches
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Address, transaction, and block searches
                </p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  {usageStats.isAdmin 
                    ? "Unlimited" 
                    : `${usageStats.usage.searches} / ${usageStats.limits.SEARCHES_PER_MONTH}`
                  }
                </div>
                {!usageStats.isAdmin && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {usageStats.remaining.searches} remaining
                  </div>
                )}
              </div>
            </div>
            {usageStats.isAdmin ? (
              <div className="h-2 bg-green-500 rounded-full"></div>
            ) : (
              <Progress 
                value={(usageStats.usage.searches / usageStats.limits.SEARCHES_PER_MONTH) * 100} 
                className="h-2"
              />
            )}
          </div>

          {/* Batch Exports */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                  Batch Exports
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Multi-address batch export reports
                </p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  {usageStats.isAdmin 
                    ? "Unlimited" 
                    : `${usageStats.usage.batchExports} / ${usageStats.limits.BATCH_EXPORTS_PER_MONTH}`
                  }
                </div>
                {!usageStats.isAdmin && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {usageStats.remaining.batchExports} remaining
                  </div>
                )}
              </div>
            </div>
            {usageStats.isAdmin ? (
              <div className="h-2 bg-green-500 rounded-full"></div>
            ) : (
              <Progress 
                value={(usageStats.usage.batchExports / usageStats.limits.BATCH_EXPORTS_PER_MONTH) * 100} 
                className="h-2"
              />
            )}
          </div>

          {/* Wallet Exports */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                  Wallet Exports
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Individual wallet address reports
                </p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  {usageStats.isAdmin 
                    ? "Unlimited" 
                    : `${usageStats.usage.walletExports} / ${usageStats.limits.WALLET_EXPORTS_PER_MONTH}`
                  }
                </div>
                {!usageStats.isAdmin && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {usageStats.remaining.walletExports} remaining
                  </div>
                )}
              </div>
            </div>
            {usageStats.isAdmin ? (
              <div className="h-2 bg-green-500 rounded-full"></div>
            ) : (
              <Progress 
                value={(usageStats.usage.walletExports / usageStats.limits.WALLET_EXPORTS_PER_MONTH) * 100} 
                className="h-2"
              />
            )}
          </div>
        </div>

        {/* Reset Information */}
        {usageStats.isAdmin ? (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-start">
              <CheckCircle2 className="h-5 w-5 text-green-500 dark:text-green-400 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-300">
                  Admin Access
                </h4>
                <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                  <p>As an administrator, you have unlimited access to all features including searches and exports.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Usage Reset Information
                </h4>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                  <p>Your usage limits reset monthly on the 1st of each month.</p>
                  <p className="mt-1">
                    <strong>Next reset:</strong> {formatResetDate(usageStats.resetDate)}
                  </p>
                  <p className="mt-2">
                    Need higher monthly limits? Request an upgrade to be an Advanced User via the Feedback page.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Profile Tab Content Component
function ProfileTabContent({ user }: { user: GetUserDto }) {
  return (
    <div className="p-6">
      <ProfileForm user={user} />
    </div>
  );
}

// Password Tab Content Component
function PasswordTabContent() {
  return (
    <div className="p-6">
      <PasswordForm />
    </div>
  );
}

// Profile Form Component
function ProfileForm({ user }: { user: GetUserDto }) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: user.username || "",
    country: user.country || "",
    phoneNumber: user.phoneNumber || ""
  });
  
  // Generate user initials for avatar fallback
  const getUserInitials = () => {
    if (user.username && user.username.length >= 2) {
      return user.username.substring(0, 2).toUpperCase();
    }
    // Fallback to email if username not available or too short
    const fallback = user.email || user.username || "U";
    return fallback.substring(0, 2).toUpperCase();
  };
  
  // Reset form data when user data changes
  useEffect(() => {
    setFormData({
      username: user.username || "",
      country: user.country || "",
      phoneNumber: user.phoneNumber ? formatPhoneNumber(user.phoneNumber) : ""
    });
  }, [user]);
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log(name, value);
    if (name === 'phoneNumber') {
      // Format the phone number as the user types
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle country selection
  const handleCountryChange = (value: string) => {
    setFormData(prev => ({ ...prev, country: value }));
  };

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: { username?: string; country?: string; phoneNumber?: string }) => {
      const res = await apiRequest("PATCH", "/api/user", data);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to update profile");
      }
      return await res.json();
    },
    onSuccess: () => {
      // Update the user data in cache
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      setIsEditing(false);
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
        variant: "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });
  
  // Handle clicking Edit Profile button
  const handleEditClick = () => {
    setIsEditing(true);
  };
  
  // Handle cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data to original values
    setFormData({
      username: user.username || "",
      country: user.country || "",
      phoneNumber: user.phoneNumber ? formatPhoneNumber(user.phoneNumber) : ""
    });
  };
  
  // Phone number validation using utility function
  const isValidPhoneNumber = (phone: string) => {
    return validatePhoneNumber(phone);
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate phone number if provided
    if (formData.phoneNumber && !isValidPhoneNumber(formData.phoneNumber)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number with country code (e.g., +1 (416) 222-3333).",
        variant: "destructive",
      });
      return;
    }
    
    // Check if there are actual changes
    const hasChanges = 
      formData.username !== (user.username || "") ||
      formData.country !== (user.country || "") ||
      formData.phoneNumber !== (user.phoneNumber || "");
    
    if (hasChanges) {
      const updateData: { username?: string; country?: string; phoneNumber?: string } = {};
      
      if (formData.username !== (user.username || "")) {
        updateData.username = formData.username;
      }
      
      if (formData.country !== (user.country || "")) {
        updateData.country = formData.country;
      }
      
      if (formData.phoneNumber !== (user.phoneNumber || "")) {
        // Strip phone number for server storage
        updateData.phoneNumber = stripPhoneNumber(formData.phoneNumber);
      }
      
      updateProfileMutation.mutate(updateData);
    } else {
      // If no changes, just exit edit mode without submitting
      setIsEditing(false);
      toast({
        title: "No changes",
        description: "No changes were made to your profile.",
        variant: "default",
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Profile Form Header */}
      <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center">
          <UserIcon className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Profile Information
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Update your personal information and how we can reach you
          </p>
        </div>
      </div>

      {/* Avatar and Created Date Header */}
      <div className="flex items-center justify-between pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2 border-gray-200 dark:border-gray-700">
            <AvatarFallback>
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Account Created
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {new Date(user.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Username
              </label>
              <Input 
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300"
                placeholder="Enter your username" 
                value={formData.username}
                name="username"
                onChange={handleChange}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
                Choose a unique username for your account
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Email Address
              </label>
              <div className="relative">
                <Input 
                  className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300"
                  placeholder="Enter your email" 
                  value={user.email} 
                  disabled 
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Badge 
                    variant="outline" 
                    className="text-xs bg-white/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 cursor-default"
                    title="This email is your primary login address"
                  >
                    Primary
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
                Email cannot be changed
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Phone Number
              </label>
              <Input 
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300"
                placeholder="Enter phone number (e.g., (416) 222-3333)" 
                value={formData.phoneNumber}
                name="phoneNumber"
                type="tel"
                onChange={handleChange}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
                Format: (416) 222-3333 - Required for SMS wallet alerts and notifications
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Country
              </label>
              <Select 
                value={formData.country} 
                onValueChange={handleCountryChange}
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300">
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="pt-6 mt-4 flex space-x-3">
            <Button 
              type="submit"
              size="sm"
              disabled={updateProfileMutation.isPending}
            >
              {updateProfileMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </Button>
            <Button 
              type="button"
              variant="outline" 
              onClick={handleCancel}
              size="sm"
              disabled={updateProfileMutation.isPending}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Username
              </label>
              <Input 
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300"
                placeholder="Enter your username" 
                value={formData.username}
                disabled
              />
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Email Address
              </label>
              <div className="relative">
                <Input 
                  className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300"
                  placeholder="Enter your email" 
                  value={user.email} 
                  disabled 
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Badge 
                    variant="outline" 
                    className="text-xs bg-white/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 cursor-default"
                    title="This email is your primary login address"
                  >
                    Primary
                  </Badge>
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Phone Number
              </label>
              <Input 
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300"
                placeholder="Your phone number" 
                value={formData.phoneNumber || "Not provided"} 
                disabled
              />
              {!formData.phoneNumber && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 ml-1">
                  Add a phone number to enable SMS wallet alerts
                </p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-2.5 text-gray-700 dark:text-gray-300">
                Country
              </label>
              <Input 
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-[#4A6CA7] text-gray-700 dark:text-gray-300"
                placeholder="Your country" 
                value={formData.country || "Not specified"} 
                disabled
              />
            </div>
          </div>
          <div className="pt-6 mt-4 flex space-x-3">
            <Button 
              type="button"
              size="sm"
              onClick={handleEditClick}
            >
              <Edit className="h-4 w-4" />
              Edit Profile
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();
  const [activeTab, setActiveTab] = useState("profile");
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Check if tab parameter is in the URL query string
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && ['profile', 'password', 'security', 'notifications', 'usage'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location]);

  // If loading, show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Redirect to="/login" />;
  }

  // Generate user initials for avatar fallback
  const getUserInitials = () => {
    if (user.username && user.username.length >= 2) {
      return user.username.substring(0, 2).toUpperCase();
    }
    // Fallback to email if username not available or too short
    const fallback = user.email || user.username || "U";
    return fallback.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-screen-md mx-auto w-full p-4 sm:p-6">
        <Card className="border-gray-200 dark:border-gray-700 shadow-md">
          <div className="flex justify-between items-center px-6 pt-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Account Settings</h3>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6 px-6">
            <TabsList className="grid w-full grid-cols-5 gap-2">
              <TabsTrigger value="profile">
                <User className="h-3.5 w-3.5 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="password">
                <Key className="h-3.5 w-3.5 mr-2" />
                Password
              </TabsTrigger>
              <TabsTrigger value="security">
                <Shield className="h-3.5 w-3.5 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger value="notifications">
                <Bell className="h-3.5 w-3.5 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="usage">
                <BarChart3 className="h-3.5 w-3.5 mr-2" />
                Usage
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile">
              <ProfileTabContent user={user} />
            </TabsContent>
            
            <TabsContent value="password">
              <PasswordTabContent />
            </TabsContent>
            
            <TabsContent value="security">
              <SecurityTabContent 
                user={user}
                showMFASetup={showMFASetup}
                setShowMFASetup={setShowMFASetup}
              />
            </TabsContent>
            
            <TabsContent value="notifications">
              <NotificationsTabContent />
            </TabsContent>
            
            <TabsContent value="usage">
              <UsageTabContent />
            </TabsContent>
          </Tabs>
        </Card>
      </div>
      
      {/* MFA Setup Dialog */}
      <Dialog open={showMFASetup} onOpenChange={setShowMFASetup}>
        <DialogContent className="sm:max-w-md md:max-w-lg">
          <MFASetup 
            onComplete={() => {
              setShowMFASetup(false);
              queryClient.invalidateQueries({ queryKey: ["/api/mfa/status"] });
            }} 
            onCancel={() => setShowMFASetup(false)} 
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
